/**
 * ============================================================================
 * THEME MANAGER
 * ============================================================================
 * 
 * Manages theme-related functionality like dark mode, color schemes, etc.
 * 
 * ============================================================================
 */



class ThemeManager {
  constructor() {
    this.currentTheme = this.getStoredTheme() || this.getSystemTheme();
    this.colorScheme = this.getStoredColorScheme() || 'default';
    
    this.init();
  }
  
  /**
   * Initialize theme manager
   */
  init() {
    this.applyTheme(this.currentTheme);
    this.applyColorScheme(this.colorScheme);
    this.bindEvents();
    
    console.log('🎨 Theme Manager initialized');
  }
  
  /**
   * Bind events
   */
  bindEvents() {
    // Theme toggle buttons
    document.addEventListener('click', (e) => {
      if (e.target.matches('[data-theme-toggle]')) {
        e.preventDefault();
        this.toggleTheme();
      }
      
      if (e.target.matches('[data-color-scheme]')) {
        e.preventDefault();
        const scheme = data(e.target, 'color-scheme');
        this.setColorScheme(scheme);
      }
    });
    
    // Listen for system theme changes
    if (window.matchMedia) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      mediaQuery.addEventListener('change', (e) => {
        if (this.currentTheme === 'auto') {
          this.applyTheme('auto');
        }
      });
    }
  }
  
  /**
   * Get system theme preference
   * @returns {string} 'light' or 'dark'
   */
  getSystemTheme() {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  }
  
  /**
   * Get stored theme from localStorage
   * @returns {string|null} Stored theme
   */
  getStoredTheme() {
    return localStorage.getItem('theme');
  }
  
  /**
   * Get stored color scheme from localStorage
   * @returns {string|null} Stored color scheme
   */
  getStoredColorScheme() {
    return localStorage.getItem('colorScheme');
  }
  
  /**
   * Set theme
   * @param {string} theme - Theme name ('light', 'dark', 'auto')
   */
  setTheme(theme) {
    this.currentTheme = theme;
    this.applyTheme(theme);
    this.storeTheme(theme);
    
    // Emit theme change event
    document.dispatchEvent(new CustomEvent('theme:change', {
      detail: { theme, resolvedTheme: this.getResolvedTheme() }
    }));
  }
  
  /**
   * Apply theme to document
   * @param {string} theme - Theme name
   */
  applyTheme(theme) {
    const resolvedTheme = this.getResolvedTheme(theme);
    
    // Remove existing theme classes
    removeClass(document.documentElement, 'theme-light');
    removeClass(document.documentElement, 'theme-dark');
    
    // Add new theme class
    addClass(document.documentElement, `theme-${resolvedTheme}`);
    
    // Set data attribute
    document.documentElement.setAttribute('data-theme', resolvedTheme);
    
    // Update theme toggle buttons
    this.updateThemeToggles();
  }
  
  /**
   * Get resolved theme (converts 'auto' to actual theme)
   * @param {string} theme - Theme name
   * @returns {string} Resolved theme
   */
  getResolvedTheme(theme = this.currentTheme) {
    if (theme === 'auto') {
      return this.getSystemTheme();
    }
    return theme;
  }
  
  /**
   * Toggle between light and dark theme
   */
  toggleTheme() {
    const newTheme = this.getResolvedTheme() === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }
  
  /**
   * Set color scheme
   * @param {string} scheme - Color scheme name
   */
  setColorScheme(scheme) {
    this.colorScheme = scheme;
    this.applyColorScheme(scheme);
    this.storeColorScheme(scheme);
    
    // Emit color scheme change event
    document.dispatchEvent(new CustomEvent('colorScheme:change', {
      detail: { colorScheme: scheme }
    }));
  }
  
  /**
   * Apply color scheme to document
   * @param {string} scheme - Color scheme name
   */
  applyColorScheme(scheme) {
    // Remove existing color scheme classes
    document.documentElement.className = document.documentElement.className
      .replace(/color-scheme-\w+/g, '');
    
    // Add new color scheme class
    if (scheme !== 'default') {
      addClass(document.documentElement, `color-scheme-${scheme}`);
    }
    
    // Set data attribute
    document.documentElement.setAttribute('data-color-scheme', scheme);
    
    // Update color scheme buttons
    this.updateColorSchemeButtons();
  }
  
  /**
   * Store theme in localStorage
   * @param {string} theme - Theme name
   */
  storeTheme(theme) {
    localStorage.setItem('theme', theme);
  }
  
  /**
   * Store color scheme in localStorage
   * @param {string} scheme - Color scheme name
   */
  storeColorScheme(scheme) {
    localStorage.setItem('colorScheme', scheme);
  }
  
  /**
   * Update theme toggle button states
   */
  updateThemeToggles() {
    const toggles = document.querySelectorAll('[data-theme-toggle]');
    const currentTheme = this.getResolvedTheme();
    
    toggles.forEach(toggle => {
      const isDark = currentTheme === 'dark';
      toggle.setAttribute('aria-pressed', isDark);
      
      // Update button text/icon if needed
      const lightText = data(toggle, 'light-text') || 'Light';
      const darkText = data(toggle, 'dark-text') || 'Dark';
      
      if (toggle.textContent.trim()) {
        toggle.textContent = isDark ? lightText : darkText;
      }
    });
  }
  
  /**
   * Update color scheme button states
   */
  updateColorSchemeButtons() {
    const buttons = document.querySelectorAll('[data-color-scheme]');
    
    buttons.forEach(button => {
      const scheme = data(button, 'color-scheme');
      const isActive = scheme === this.colorScheme;
      
      button.setAttribute('aria-pressed', isActive);
      
      if (isActive) {
        addClass(button, 'active');
      } else {
        removeClass(button, 'active');
      }
    });
  }
  
  /**
   * Get current theme
   * @returns {string} Current theme
   */
  getCurrentTheme() {
    return this.currentTheme;
  }
  
  /**
   * Get current color scheme
   * @returns {string} Current color scheme
   */
  getCurrentColorScheme() {
    return this.colorScheme;
  }
  
  /**
   * Check if dark mode is active
   * @returns {boolean} True if dark mode is active
   */
  isDarkMode() {
    return this.getResolvedTheme() === 'dark';
  }
}
